import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { anredeArray } from "../../../components/form/fields/inputFields";
import SelectField from "../../../components/form/fields/SelectField";
import TextField from "../../../components/form/fields/TextField";
import DEDateField from "../../../components/form/fields/DEDateField";
import IBANTextField from "../../../components/form/fields/IBANTextField";
import { formatIBAN, getDateFromDateField } from "../../../utils/dataUtils";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getDefaultValues = (contract) => {
  const standortRef = contract?.standortRef?._id || contract?.standortRef || "";
  return {
    title: contract?.title || "",
    firstName: contract?.firstName || "",
    lastName: contract?.lastName || "",
    birthdate: contract?.birthdate || null,
    isOwner: typeof contract?.isOwner === "boolean" ? contract.isOwner : true,
    owner: contract?.owner || "",
    bank: contract?.bank || "",
    iban: contract?.iban ? formatIBAN(contract.iban) : "",
    bic: contract?.bic || "",
    email: contract?.email || "",
    phoneNumber: contract?.phoneNumber || "",
    mobile: contract?.mobile || "",
    street: contract?.street || "",
    zip: contract?.zip || "",
    city: contract?.city || "",
    standortRef,
  };
};

export default function ContractFormDialog({
  open,
  mode,
  contract,
  sites,
  onClose,
  onSubmit,
  isSaving,
}) {
  const isEdit = mode === "edit";
  const hasMembers = (contract?.hasMembers || []).length > 0;
  const disableSiteChange = isEdit && hasMembers;

  const defaultValues = useMemo(() => getDefaultValues(contract), [contract]);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  const isOwner = watch("isOwner");
  const firstName = watch("firstName");
  const lastName = watch("lastName");

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset, open]);

  useEffect(() => {
    if (isOwner) {
      const ownerName = `${lastName || ""} ${firstName || ""}`.trim();
      if (ownerName) {
        setValue("owner", ownerName);
      }
    }
  }, [firstName, lastName, isOwner, setValue]);

  const handleFormSubmit = (data) => {
    const payload = { ...data };
    payload.birthdate = getDateFromDateField(payload.birthdate);

    if (payload.iban) {
      payload.iban = payload.iban.replace(/\s/g, "").toUpperCase();
    }

    if (payload.isOwner && !payload.owner) {
      payload.owner = `${payload.lastName || ""} ${payload.firstName || ""}`.trim();
    }

    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? "Vertrag bearbeiten" : "Neuen Vertrag anlegen"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {isEdit && contract?.publicRegistrationMeta?.createdViaPublic && (
            <Alert severity="info">
              Dieser Vertrag wurde über eine öffentliche Anmeldung erstellt.
            </Alert>
          )}

          {disableSiteChange && (
            <Alert severity="warning">
              Standort kann nicht geändert werden, solange Mitglieder zugeordnet
              sind.
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Vertragsperson
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SelectField
                  control={control}
                  name="title"
                  label="Anrede"
                  itemArray={anredeArray}
                  required={false}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  control={control}
                  name="lastName"
                  label="Nachname"
                  required="Bitte den Nachnamen eingeben."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  control={control}
                  name="firstName"
                  label="Vorname"
                  required="Bitte den Vornamen eingeben."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DEDateField
                  control={control}
                  name="birthdate"
                  label="Geburtstag"
                  required={false}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: 1.5,
                    bgcolor: "background.neutral",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Controller
                        name="isOwner"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        )}
                      />
                    }
                    label="Vertragsnehmer ist Kontoinhaber"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Ist der Schalter aktiv, wird der Kontoinhaber automatisch
                    aus Vor- und Nachname übernommen.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Kontakt
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  control={control}
                  name="email"
                  label="E-Mail"
                  fullWidth
                  rules={{
                    pattern: {
                      value: emailRegex,
                      message: "Bitte eine gültige E-Mail eingeben.",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  control={control}
                  name="phoneNumber"
                  label="Telefon"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  control={control}
                  name="mobile"
                  label="Mobil"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  control={control}
                  name="street"
                  label="Straße, Hausnummer"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  control={control}
                  name="zip"
                  label="PLZ"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  control={control}
                  name="city"
                  label="Ort"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Bankdaten
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: "background.neutral",
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    control={control}
                    name="owner"
                    label="Kontoinhaber"
                    fullWidth
                    disabled={isOwner}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    control={control}
                    name="bank"
                    label="Kreditinstitut"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <IBANTextField
                    control={control}
                    name="iban"
                    label="IBAN"
                    fullWidth
                    setError={setError}
                    clearErrors={clearErrors}
                    required="Bitte eine IBAN eingeben."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    control={control}
                    name="bic"
                    label="BIC"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Zuordnung
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(errors.standortRef)}>
                  <InputLabel id="contract-site-label">Standort</InputLabel>
                  <Controller
                    name="standortRef"
                    control={control}
                    rules={{
                      required: "Bitte einen Standort auswählen.",
                    }}
                    render={({ field }) => (
                      <Select
                        labelId="contract-site-label"
                        label="Standort"
                        value={field.value || ""}
                        onChange={field.onChange}
                        disabled={disableSiteChange}
                      >
                        {sites.map((site) => (
                          <MenuItem key={site._id} value={site._id}>
                            {site.siteName}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>
                    {errors.standortRef?.message ||
                      (disableSiteChange
                        ? "Standortwechsel nur möglich, wenn keine Mitglieder zugeordnet sind."
                        : "Standort auswählen")}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          variant="contained"
          disabled={isSaving}
        >
          {isEdit ? "Änderungen speichern" : "Vertrag anlegen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ContractFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
  contract: PropTypes.object,
  sites: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

ContractFormDialog.defaultProps = {
  contract: null,
  sites: [],
  isSaving: false,
};
